import { AppDispatch } from '../../store/store';
import {
  removeFromHand,
  resetGameState,
  setGameId,
  setHand,
  setMiddle,
  setOfflineMode,
  setOpenGameOverModal,
  setPlayersInitially,
  resetPlayerCardCount,
  setPlayerTurn,
  setSomethingHappend
} from '../../store/gameSlice';
import { setUsername, reset as resetOnlineUsers } from '../../store/onlineUsersSlice';
import { setGameData, resetGame as resetGameSummary } from '../../store/gameSummarySlice';
import { applyThrowEffect } from '../../message-handler/gameEffects';
import {
  BotDifficulty,
  CardDto,
  computeScore,
  createOfflineGame,
  getCurrentPlayer,
  isBot,
  OfflineGameState,
  pickBotCard,
  throwCard,
  withRotation
} from './offlineEngine';

export type { BotDifficulty };

export const HUMAN_PLAYER = 'You';
const TEAM1 = [HUMAN_PLAYER, '#BOT#T1P2'] as [string, string];
const TEAM2 = ['#BOT#T2P1', '#BOT#T2P2'] as [string, string];
const BOT_THINK_DELAY_MS = 1400;

let activeGame: OfflineGameState | null = null;
let botDifficulty: BotDifficulty = 'medium';

// Serializes every throw (human clicks + scheduled bot moves) onto a single chain so that
// two throwAndApply calls can never interleave and race on the shared mutable engine state.
// isThrowPending additionally blocks a second human click from queuing up while an earlier
// throw's capture animation (ZNK/SLICE) is still draining — playerTurn flips to the human
// synchronously before that animation finishes, so the card stays visually clickable for up
// to ~1s before the queued throw actually runs.
let pendingChain: Promise<void> = Promise.resolve();
let isThrowPending = false;
const enqueueThrow = (task: () => Promise<void>): void => {
  isThrowPending = true;
  pendingChain = pendingChain
    .then(task, task)
    .catch(error => console.error(error))
    .finally(() => { isThrowPending = false; });
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

export const startOfflineGame = (dispatch: AppDispatch, difficulty: BotDifficulty = 'medium'): void => {
  dispatch(resetGameState());
  dispatch(resetGameSummary());
  dispatch(resetOnlineUsers());

  botDifficulty = difficulty;
  const gameId = `offline-${Date.now()}`;
  activeGame = createOfflineGame(gameId, TEAM1, TEAM2);

  dispatch(setUsername({ username: HUMAN_PLAYER }));
  dispatch(setOfflineMode(true));
  dispatch(setGameId({ gameId }));
  dispatch(setPlayersInitially({ players: activeGame.turnOrder }));
  dispatch(setHand({ hand: activeGame.remainingHand[HUMAN_PLAYER].map(withRotation) }));
  dispatch(setMiddle({ middle: activeGame.middle.map(withRotation) }));
  dispatch(setPlayerTurn({ player: getCurrentPlayer(activeGame) }));

  maybeRunBotTurn(dispatch);
};

const finishOfflineGame = async (dispatch: AppDispatch): Promise<void> => {
  if (!activeGame) return;
  dispatch(setSomethingHappend({ message: 'KRAJ' }));
  await delay(1000);
  dispatch(setSomethingHappend({ message: null }));
  dispatch(setOpenGameOverModal({ value: true }));
  dispatch(setGameData({ gameData: computeScore(activeGame) }));
};

const throwAndApply = async (dispatch: AppDispatch, playerName: string, card: CardDto): Promise<void> => {
  if (!activeGame) return;

  let result;
  try {
    result = throwCard(activeGame, playerName, card);
  } catch (error) {
    console.error(error);
    return;
  }

  if (playerName === HUMAN_PLAYER) {
    dispatch(removeFromHand({ rank: card.rank, suit: card.suit }));
  }

  // Keep the scoreboard live: recompute after every throw, not just at game over.
  dispatch(setGameData({ gameData: computeScore(activeGame) }));

  await applyThrowEffect(dispatch, result);

  if (result.roundState === 'ROUND_FINISHED') {
    dispatch(setHand({ hand: activeGame.remainingHand[HUMAN_PLAYER].map(withRotation) }));
    // Online this rides on the server's private DEAL message. Offline there is no
    // such message, so without this the opponents' card counters stay at zero and
    // every seat reads "empty" for the rest of the game.
    dispatch(resetPlayerCardCount());
  }

  if (result.roundState === 'GAME_OVER') {
    await finishOfflineGame(dispatch);
    return;
  }

  maybeRunBotTurn(dispatch);
};

const maybeRunBotTurn = (dispatch: AppDispatch): void => {
  if (!activeGame) return;
  const current = getCurrentPlayer(activeGame);
  if (!isBot(current)) return;

  setTimeout(() => {
    enqueueThrow(() => {
      if (!activeGame) return Promise.resolve();
      const stillCurrent = getCurrentPlayer(activeGame);
      if (stillCurrent !== current) return Promise.resolve();
      const card = pickBotCard(activeGame, current, botDifficulty);
      return throwAndApply(dispatch, current, card);
    });
  }, BOT_THINK_DELAY_MS);
};

/** Called by GameBoard when the human player clicks a card. Mirrors the online axios throw call. */
export const offlineThrowCard = (dispatch: AppDispatch, rank: string, suit: string): void => {
  if (isThrowPending) return; // a throw is already in flight (e.g. a capture animation draining) — ignore the extra click
  enqueueThrow(() => throwAndApply(dispatch, HUMAN_PLAYER, { rank, suit }));
};

export const stopOfflineGame = (): void => {
  activeGame = null;
};
